'use client'
import { Typography, styled } from '@mui/material'
import HeadingWithParagraphs from '@/UI/Paragraph/HeadingWithParagraphs'
import Image from 'next/image'
import PaddedGrid from '@/UI/Layout/Grid/PaddedGrid'

const ListItem = styled('ul')(() => ({}))

const SignatureWrapper = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(7),
  '& .image-wrapper': {
    minHeight: '50px'
  }
}))

const PageContent = () => {
  return (
    <>
      <Typography variant="h1" align="center">
        Modern Slavery Statement
      </Typography>
      <PaddedGrid columns={12}>
        <Typography variant="h2">Zuto Limited Modern Slavery Statement – November 2025</Typography>
        <HeadingWithParagraphs
          title="1. Overview"
          paragraphs={[
            'In compliance with the Modern Slavery Act 2015, this statement outlines the steps taken by Zuto Limited (Zuto) during the financial year ending in June 2025 to identify, prevent, and mitigate the risks of Modern Slavery and Human Trafficking (MSHT) in both our company and our supply chains.',
            'Zuto maintains a zero-tolerance approach to modern slavery and human trafficking. We do not use, support, or engage in any form of forced, child, or involuntary labour across any part of our operations or supply chain. We comply with the Modern Slavery Act 2015 and all other relevant anti-slavery and human trafficking laws and regulations.'
          ]}
          sectionId="overview"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="2. Our Business"
          paragraphs={[
            'Zuto Ltd is an FCA authorised motor finance broker that specialises in securing vehicle finance for our customers by finding them the best finance options for their specific needs and financial situation in an easy and simple way. As of October 2025, Zuto employed c.530 full-time employees.',
            'Zuto is a certified B Corporation, reflecting our commitment to the highest standards of social and environmental performance, accountability, and transparency. This accreditation recognises our dedication to responsible business practices across all areas of our operations, from employee wellbeing to community engagement and charitable initiatives. We have proudly maintained B Corp certification since March 2022.'
          ]}
          styleOverrides={{ marginBottom: 0 }}
          sectionId="our-business"
          hasAnchor
        />
        <Typography variant="body1">
          Zuto operates solely within the UK and is made up of the following companies:
        </Typography>
        <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
          <li>
            Zuto Limited (Company number 05722976) through which we provide our vehicle finance
            credit broking and insurance broking services.
          </li>
          <li>
            Zuto Holdings Limited (Company number 08563366) which is our ultimate parent company.
          </li>
        </ListItem>
        <Typography variant="body1" sx={{ marginBottom: (theme) => theme.spacing(7) }}>
          Zuto&apos;s supply chains include a wide range of suppliers of goods and services
          including our lenders, lead generation sources, IT software, professional services, and
          office consumables.
        </Typography>
        <HeadingWithParagraphs
          title="3. Risk Assessment"
          paragraphs={[
            'As we operate solely within the UK and given the nature of our business as a motor finance broker, we believe the opportunity for modern slavery to occur either within our business or our supply chains to be low. Furthermore, as we are not a manufacturer of physical products, we do not rely on manual labour or overseas outsourcing of production within our operations. Zuto is dedicated to creating a safe workplace environment ensuring that we minimise the risk of MSHT within both our organisation and our supply chains.',
            'Zuto takes all reasonable steps to avoid causing or contributing to adverse human rights impacts that may arise from our operations, products, services, or business relationships. Where any risk of modern slavery or human trafficking is identified, we will conduct a detailed risk assessment and will act diligently to help mitigate/remediate any impact that may occur.'
          ]}
          sectionId="risk-assessment"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="4. Supply Chain Due Diligence"
          paragraphs={[
            'As part of our contract approval process for onboarding all new prospective partners, Zuto undertakes due diligence and gives consideration to the practices, procedures and controls of any partners to ensure that they are reputable businesses who evidence the right values in line MSHT requirements.',
            'We also have an enhanced ongoing annual due diligence approach for our lender and lead generation partners which includes consideration of their MSHT practices and policies and agreeing contractual obligations which include industry standard terms to ensure they meet the requirements of Modern Slavery legislation and Zuto’s minimum standards.'
          ]}
          sectionId="supply-chain-due-diligence"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="5. Policies Relating to MSHT"
          paragraphs={[
            'MSHT forms part of our overall Governance and Risk Management Frameworks. Our policies and procedures are designed to ensure that we conduct our business ethically and responsibly towards our customers, employees, and suppliers. To enable us to do this, we maintain a suite of policies which help mitigate the risk of MSHT, either directly within our operations or through our supply chains. These policies are reviewed on an annual basis. Relevant policies include:'
          ]}
          styleOverrides={{ marginBottom: 0 }}
          sectionId="policies-relating-to-msht"
          hasAnchor
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
          <li>Inclusive Recruitment & Progression Policy</li>
          <li>Working Hours and Overtime Policy</li>
          <li>Environmental Preferable Purchasing Policy</li>
          <li>Remuneration Policy</li>
          <li>Dignity & Respect Policy</li>
          <li>Anti-Harassment & Bullying Policy</li>
          <li>Equal Opportunities Policy</li>
          <li>Whistleblowing Policy</li>
          <li>Contract Approval Process</li>
          <li>Anti-Money Laundering & Anti-Bribery Policies</li>
          <li>Work Experience Policy</li>
          <li>Performance Talent Management Policy</li>
        </ListItem>
        <Typography variant="body1" sx={{ marginBottom: (theme) => theme.spacing(7) }}>
        Zuto has a robust onboarding process which includes DBS and Right to Work checks. Enhanced support is
        offered to all employees through our Wellbeing Network, Mental Health First Aiders, and Employee
        Assistance Programme initiatives.
        </Typography>
        <HeadingWithParagraphs
          title="6. Training Relating to MSHT"
          paragraphs={[
            'Our suite of policies is supported by a framework of mandatory and tailored training to ensure awareness of key risks across the organisation. As part of our training, we encourage all employees to raise concerns of any wrongdoings as part of our ongoing supportive culture of doing the right thing.'
          ]}
          sectionId="training-relating-to-msht"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="7. Enhancements Over the Last 12 Months"
          paragraphs={[
            'Over the past year, we have strengthened our control framework by undertaking a comprehensive refresh of our annual lender and partner due diligence processes. This included updating our due diligence documentation and MI requirements to obtain greater insight into our lenders’ and partners’ operations, as well as their policies and practices relating to modern slavery and human trafficking.',
            'Additionally, we have enhanced our commitment to employee wellbeing by introducing comprehensive new policies and procedures which cover working hours and overtime management, and remuneration. We have also reinforced our safeguarding standards by strengthening the criteria on DBS checks for employees. These initiatives reflect our proactive approach to fostering a safe, supportive, and fair workplace.',
            'Further demonstrating this commitment, Zuto has successfully achieved ISO 45003 Wellbeing Certification, a globally recognised standard for promoting psychological health and wellbeing at work.'
          ]}
          styleOverrides={{ marginBottom: 0 }}
          sectionId="enhancements-over-the-last-12-months"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="8. Ongoing Monitoring and Our Continued Commitment"
          paragraphs={[
            'As part of our ongoing oversight of MSHT risks, we will continue to ensure the following:'
          ]}
          styleOverrides={{ marginBottom: 0 }}
          sectionId="ongoing-monitoring-and-our-continued-commitment"
          hasAnchor
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
          <li>Conduct annual reviews of all policies and training material and performance;</li>
          <li>Undertake ongoing due diligence of all third party partners;</li>
          <li>Consider key MSHT risks as part of our broader risk management framework; and</li>
          <li>Monitor the % of employees who have completed and passed required annual training refreshers.</li>
        </ListItem>
        <Typography variant="body1">
          Whilst we have had no incidents of slavery or human trafficking within our organisation
          and are not aware of any incidents occurring within our supply chain, we remain committed
          to improving our controls and processes where we identify opportunities for these to be
          enhanced.
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: (theme) => theme.spacing(5) }}>
          This statement in relation to the Zuto&apos;s 2025 financial year was approved by the
          Board of Directors of Zuto Limited on 30th November 2025.
        </Typography>
        <SignatureWrapper>
          <div className="image-wrapper">
            <Image
              src="/images/JamesWilkinsonSignature.png"
              alt="James Wilkinson Signature"
              width={85}
              height={50}
            />
          </div>
          <Typography variant="body1" mb="0">
            James Wilkinson
          </Typography>
          <Typography variant="body1">Chief Executive Officer</Typography>
        </SignatureWrapper>
        <Typography variant="body1">Last updated 12th January 2026</Typography>
      </PaddedGrid>
    </>
  )
}

export default PageContent
